<template>
    <div class="HaulingReport">
        <div v-for="ticket in report.tickets" :key="ticket.id">
            <div class="page" :class="{ isFirstPage: report.tickets.indexOf(ticket) === 0 }">
                <div class="companyHeader">
                    <div class="companyProfile">
                        <CompanyLogo v-if="report.company.logo"/>
                        <div>
                            <h3 v-if="report.company && report.company.name">{{ report.company.name }}</h3>
                            <div class="address">
                                <p v-if="report.companyOwner && report.companyOwner.address">
                                    {{ `${report.companyOwner.address},` }}
                                </p>
                                <p v-if="
                                    report.companyOwner &&
                                    report.companyOwner.city &&
                                    report.companyOwner.state &&
                                    report.companyOwner.zip
                                ">
                                    {{ `${report.companyOwner.city}, ${report.companyOwner.state} ${report.companyOwner.zip}` }}
                                </p>
                            </div>
                            <p v-if="report.companyOwner && report.companyOwner.phone">
                                {{ `Phone: ${report.companyOwner.phone}` }}
                            </p>
                        </div>
                    </div>
                    <h1>Hauling Sheet</h1>
                </div>
                <div class="metaHeader meta">
                    <div class="top">
                        <ReportMetaRow title="Ticket Number" :value="ticket.ticketNumber" />
                    </div>
                    <div class="half">
                        <ReportMetaRow title="Ticket Date" :value="formatDate(ticket.submittedAt, timeZone)" />
                        <ReportMetaRow title="Driver" :value="formatDriverName(ticket)" />
                        <ReportMetaRow title="Truck" :value="ticket.truck.name" />
                        <ReportMetaRow title="Trailer" :value="ticket.trailer ? ticket.trailer.name : ''" />
                    </div>
                    <div class="half">
                        <ReportMetaRow title="Project Name" :value="ticket.project.name" />
                        <ReportMetaRow title="Customer" :value="ticket.project.client.name" />
                        <ReportMetaRow title="Load Site" :value="truncate(ticket.project.loadSite, { length: 35 })" />
                        <ReportMetaRow title="Dump Site" :value="truncate(ticket.project.dumpSite, { length: 35 })" />
                    </div>
                </div>
                <table class="main">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th colspan="2">Load Site In / Out</th>
                            <th colspan="2">Dump Site In / Out</th>
                            <th>Material</th>
                            <th>{{ isClarkLlc ? 'Cubic Yards' : 'Tons' }}</th>
                            <th>Scale Ticket #</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="load in ticket.loads" :key="load.id">
                            <td width="1%">{{ load.number }}</td>
                            <td width="8%">{{ formatTime(load.loadSiteArriveAt, timeZone) }}</td>
                            <td width="8%">{{ formatTime(load.loadSiteLeaveAt, timeZone) }}</td>
                            <td width="8%">{{ formatTime(load.dumpSiteArriveAt, timeZone) }}</td>
                            <td width="8%">{{ formatTime(load.dumpSiteLeaveAt, timeZone) }}</td>
                            <td width="19%">{{ load.isBackhaul ? 'Backhaul - ' : '' }}{{ load.material.name }}</td>
                            <td width="4%">{{ load.tons | formatTons }}</td>
                            <td width="12%">{{ load.scaleTicketCode }}</td>
                            <td width="32%">{{ truncate(load.notes, { length: 120 }) }}</td>
                        </tr>
                        <tr v-for="row in createEmptyRows(ticket.loads.length)" :key="row">
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                    </tbody>
                </table>
                <div class="metaFooter meta">
                    <div class="third">
                        <ReportMetaRow title="Start Precheck" :value="ticketHasReportTimeOverrides(ticket) ? '' : formatTime(ticket.precheckStartAt, timeZone)" />
                        <ReportMetaRow title="Leave Yard" :value="ticketHasReportTimeOverrides(ticket) ? '' : formatTime(ticket.yardLeaveAt, timeZone)" />
                        <ReportMetaRow title="Arrive at Yard" :value="ticketHasReportTimeOverrides(ticket) ? '' : formatTime(ticket.yardArriveAt, timeZone)" />
                        <ReportMetaRow title="Complete Postcheck" :value="ticketHasReportTimeOverrides(ticket) ? '' : formatTime(ticket.postcheckEndAt, timeZone)" />
                        <ReportMetaRow title="Indirect Hours" :value="roundToNearestDecimalPlace(ticket.indirectHours)" />
                    </div>
                    <div class="third">
                        <ReportMetaRow title="Direct Hours" :value="roundToNearestDecimalPlace(ticket.directHours)" />
                        <ReportMetaRow title="Total Hours" :value="roundToNearestDecimalPlace(ticket.indirectHours + ticket.directHours)" />
                        <ReportMetaRow
                            :title="isClarkLlc ? 'Cubic Yards' : 'Tons'" :value="ticket.totalTons | formatTons"
                        />
                        <ReportMetaRow title="Number of Loads" :value="ticket.loads.length" />
                        <ReportMetaRow title="Avg Loop Hours" :value="ticket.averageLoopHours" />
                    </div>
                    <div class="third">
                        <ReportMetaRow title="Odometer Start" :value="ticket.odometerPrecheck" />
                        <ReportMetaRow title="Odometer End" :value="ticket.odometerPostcheck" />
                        <ReportMetaRow title="Miles Driven" :value="ticket.odometerPostcheck - ticket.odometerPrecheck" />
                    </div>
                </div>
                <div class="ticketNotes meta">
                    <ReportMetaRow title="Ticket Notes" :value="truncate(ticket.notes, { length: 240 })" />
                </div>
                <div class="footer">
                    <div class="footerLeft">
                        <div class="half">
                            <div v-if="ticket.signatureUrl" class="signatureContainer">
                                <span>Driver Signature:</span>
                                <div class="signatureImage">
                                    <img class="logo" :src="ticket.signatureUrl" />
                                </div>
                            </div>
                        </div>
                        <div class="half">
                            <div v-if="ticket.customerSignatureUrl" class="signatureContainer">
                                <span>Customer Signature:</span>
                                <div class="signatureImage">
                                    <img class="logo" :src="ticket.customerSignatureUrl" />
                                </div>
                            </div>
                        </div>
                        <div v-if="ticket.signatureUrl || ticket.customerSignatureUrl" class="acknowledgement">
                            Upon acknowledgement, the signatories above certify the information set forth on this form is
                            correct and accurate to the best of their knowledge and that the work was completed in a
                            satisfactory manner.
                        </div>
                    </div>
                    <div class="footerRight">
                        <div class="poweredBy">
                            <div class="logoContainer">
                                <div>Powered by</div>
                                <Logo />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="report.includePhotos">
                <div v-for="load in ticket.loads" :key="load.id">
                    <div v-if="load.photo" class="page pageLoad">
                        <div class="loadTitle">Load #{{ load.number }}</div>
                        <img :src="loadPhotoUrl(load.photo)"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import truncate from 'lodash/truncate';
import { getApiImgPath, formatDate, formatTime, roundToNearestDecimalPlace } from '@/utils/formatting';
import Logo from '@/components/Logo';
import CompanyLogo from '@/components/CompanyLogo';
import ReportMetaRow from './ReportMetaRow';

export default {
    name: 'HaulingReport',

    components: { Logo, CompanyLogo, ReportMetaRow },

    props: {
        report: { type: Object, required: true },
        timeZone: { type: String, required: true },
        isClarkLlc: { type: Boolean, default: false },
    },

    methods: {
        truncate,
        formatDate,
        formatTime,
        roundToNearestDecimalPlace,

        formatDriverName(ticket) {
            const formattedName = `${ticket.user.firstName} ${ticket.user.lastName}`;

            const employeeCode = ticket.user.driverInfo?.employeeCode;
            if (employeeCode) {
                return `${employeeCode} - ${formattedName}`;
            }

            return formattedName;
        },

        createEmptyRows(count) {
            return Array.from({ length: 15 - count });
        },

        loadPhotoUrl(fileName) {
            return getApiImgPath(fileName);
        },

        ticketHasReportTimeOverrides(ticket) {
            const reportTimeOverrides = ticket.project.reportTimeOverrides;
            return Object.values(reportTimeOverrides).some((value) => value !== null);
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';
$load-page-header-height: $admin-gutter;

.HaulingReport {
    .page {
        display: flex;
        flex-direction: column;
    }

    .page:not(.isFirstPage),
    .pageLoad {
        @media not print {
            border-top: 2px solid #000;
            padding-top: $admin-gutter * 2;
            margin-top: $admin-gutter * 2;
        }
    }

    .pageLoad {
        .loadTitle {
            height: $load-page-header-height;
            font-size: 18px;
            font-weight: bold;
        }

        img {
            width: 100%;
            height: calc(100vh - #{$load-page-header-height});
        }
    }

    .companyHeader {
        display: flex;
        justify-content: space-between;
    }

    .CompanyLogo {
        width: 75px;
        height: 75px;
        margin-right: 20px;
        background-size: cover;
        background-position: center;

        ::v-deep .logo {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            border-radius: 0;
        }
    }

    .companyProfile {
        display: flex;
    }

    h3, .address {
        margin-bottom: 5px;
    }

    .metaHeader .top .ReportMetaRow {
        width: 50%;
        margin-left: auto;
        margin-bottom: 20px;
    }

    .metaHeader .half {
        float: right;
    }

    .metaHeader .half .ReportMetaRow ::v-deep td:first-child {
        width: 30%;
    }

    .metaHeader .half .ReportMetaRow ::v-deep td:last-child {
        width: 70%;
    }

    .main {
        margin: 20px 0;
    }

    .ticketNotes {
        margin-top: 20px;
    }
    .ticketNotes .ReportMetaRow ::v-deep td:first-child {
        width: 20%;
    }
    .ticketNotes .ReportMetaRow ::v-deep td:last-child {
        width: 80%;
    }

    .footer {
        display: flex;
        align-items: flex-end;
        flex: 1;

        .footerLeft {
            flex: 1;
        }

        .footerRight {
            width: 20%;
            position: relative;
        }
    }

    .poweredBy {
        width: 100%;
        padding-left: $grid-gutter-width;
        margin-bottom: $grid-gutter-width * 2;

        .logoContainer {
            width: 100%;
            text-align: center;
        }

        .logoContainer div {
            font-weight: bold;
            font-style: italic;
            margin-bottom: 5px;
        }

        .logoContainer .Logo {
            width: 100%;
        }
    }

    .acknowledgement {
        width: 100%;
        float: left;
        font-size: 12px;
    }

    .signatureContainer {
        margin: 10px 0;
    }

    .signatureContainer span {
        display: inline-block;
        vertical-align: bottom;
    }

    .signatureImage {
        display: inline-block;
        vertical-align: bottom;
        margin-left: 10px;
        border-bottom: 1px solid #000000;
        width: 160px;
        height: 90px;
        overflow: hidden;
    }

    .signatureImage img {
        display: block;
        width: 100%;
    }
}
</style>
